import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { getMyProfile, login, logout, refreshLogin } from "../../api";
import { UserProfile } from "../../api/me.generated";
import { getRefreshToken } from "../../hooks/token";
import type { RootState } from "../../stores/store";

interface AuthError {
  code: number;
  message: string;
}

interface AuthSuccess {
  access_token: string;
  refresh_token: string;
}

interface AuthState {
  isLoggedIn: boolean;
  isInProgress: boolean;
  isInitialized: boolean;
  roleId: string;
  user: UserProfile;
  error: AuthError;
}

const initialState: AuthState = {
  user: {
    name: "",
    email: "",
  },
  roleId: "",
  isInitialized: false,
  isInProgress: false,
  isLoggedIn: false,
  error: {
    code: 0,
    message: "",
  },
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    start: (state: AuthState) => {
      return {
        ...state,
        isInProgress: true,
      };
    },
    success: (state: AuthState, action: PayloadAction<AuthSuccess>) => {
      return {
        ...state,
        isInProgress: false,
        isLoggedIn: true,
      };
    },
    error: (state: AuthState, action: PayloadAction<AuthError>) => {
      return {
        ...state,
        isInProgress: false,
        error: action.payload,
      };
    },
    logoutStart: (state: AuthState) => {
      return {
        ...state,
        isInProgress: true,
      };
    },
    logoutSuccess: (state: AuthState) => {
      return {
        ...state,
        user: initialState.user,
        isInProgress: false,
        isLoggedIn: false,
      };
    },
    logoutError: (state: AuthState) => {
      return {
        ...state,
        user: {},
        isInProgress: false,
        isLoggedIn: false,
      };
    },
    initializationStart: (state: AuthState) => {
      return {
        ...state,
        isInProgress: true,
      };
    },
    initializationSuccess: (
      state: AuthState,
      action: PayloadAction<UserProfile>
    ) => {
      return {
        ...state,
        isInProgress: false,
        isLoggedIn: true,
        isInitialized: true,
        user: action.payload,
      };
    },
    initializationError: (
      state: AuthState,
      action: PayloadAction<AuthError>
    ) => {
      return {
        ...state,
        isInProgress: false,
        isInitialized: true,
        error: action.payload,
      };
    },
  },
});

export const authenticateUser =
  ({ username, password }: any) =>
  async (dispatch: any) => {
    try {
      dispatch(start());
      const authData = await login(username, password);
      dispatch(success(authData));
      // const { t } = useTranslation();
      // toast.success(t("toast.login.success"));
      dispatch(initialize());
    } catch (err: any) {
      dispatch(error(err.response.data));
    }
  };

export const logoutUser = () => async (dispatch: any) => {
  try {
    dispatch(logoutStart());
    await logout();
    dispatch(logoutSuccess());
  } catch (e: any) {
    dispatch(logoutError());
  }
};

export const initialize = () => async (dispatch: any) => {
  let tryRefresh = false;
  let error;
  dispatch(initializationStart());
  try {
    const profileData = await getMyProfile();
    dispatch(initializationSuccess(profileData));
  } catch (err: any) {
    tryRefresh = true;
    error = err;
  }

  if (tryRefresh) {
    const token = getRefreshToken() ?? "";
    if (token) {
      try {
        await refreshLogin(token);
        const profileData = await getMyProfile();
        dispatch(initializationSuccess(profileData));
      } catch (err: any) {
        dispatch(initializationError(err.response.data));
      }
    } else {
      dispatch(
        initializationError({
          code: 0,
          message: "",
        })
      );
    }
  } else {
    dispatch(initializationError(error?.response?.data ?? error?.response));
  }
};

export const {
  start,
  success,
  error,
  logoutStart,
  logoutSuccess,
  logoutError,
  initializationStart,
  initializationSuccess,
  initializationError,
} = authSlice.actions;

export const authSelector = (state: RootState) => state.auth;

export const authReducer = authSlice.reducer;
